import { useId, useRef, useState } from 'react';
import {
    IMAGE_FRAMING_CENTERED,
    IMAGE_FRAMING_MAX_ZOOM,
    IMAGE_FRAMING_MIN_ZOOM,
    clampFramingPosition,
    clampFramingZoom,
    getVisibleImageArea,
} from '../../../utils/ImageFramingUtils';
import './ImageFocusEditor.css';

// Deslocamento máximo, em px, para um toque ainda contar como toque e não como rolagem da página.
const TAP_TOLERANCE = 8;
const ZOOM_STEP = 10;

/**
 * Editor de enquadramento de uma foto de capa.
 *
 * Mostra a foto inteira com o ponto de foco (o que não pode ser cortado) e as molduras de cada
 * formato em que ela aparece, já recalculadas com o zoom. O foco e o zoom são os mesmos que
 * getImageFramingStyle aplica na página, então a moldura desenhada é o recorte real.
 *
 * @param {string} src - Foto a enquadrar.
 * @param {{positionX: number, positionY: number, zoom: number}} framing - Enquadramento atual.
 * @param {function} onChange - Recebe o enquadramento completo a cada ajuste.
 * @param {Array} frames - { key, label, aspectRatio } de cada formato (largura / altura).
 * @param {function} onImageLoad - Recebe { width, height } reais da foto quando ela carrega.
 */
const ImageFocusEditor = ({ src, alt = '', framing = IMAGE_FRAMING_CENTERED, onChange, frames = [], onImageLoad }) => {
    const zoomInputId = useId();
    const stageRef = useRef(null);
    const tapStartRef = useRef(null);
    const [imageAspect, setImageAspect] = useState(null);
    const [dragging, setDragging] = useState(false);

    const positionX = clampFramingPosition(framing.positionX);
    const positionY = clampFramingPosition(framing.positionY);
    const zoom = clampFramingZoom(framing.zoom);

    const emit = (next) => onChange?.({ positionX, positionY, zoom, ...next });

    // Converte o ponto da tela em percentual da foto; o palco tem exatamente o tamanho dela.
    const moveFocusTo = (clientX, clientY) => {
        const rect = stageRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0) {
            return;
        }
        emit({
            positionX: clampFramingPosition(((clientX - rect.left) / rect.width) * 100),
            positionY: clampFramingPosition(((clientY - rect.top) / rect.height) * 100),
        });
    };

    /*
     * Mouse e caneta arrastam em qualquer ponto da foto. No toque, a foto ocupa boa parte da tela
     * do celular: arrastar sobre ela precisa continuar rolando a página. Por isso o toque na foto
     * só posiciona o foco ao soltar sem ter rolado, e quem quer arrastar usa o marcador, que tem
     * touch-action: none.
     */
    const handlePointerDown = (event) => {
        const onHandle = Boolean(event.target.closest('.image-focus-editor__handle'));
        if (event.pointerType === 'touch' && !onHandle) {
            tapStartRef.current = { x: event.clientX, y: event.clientY };
            return;
        }

        event.preventDefault();
        stageRef.current?.setPointerCapture?.(event.pointerId);
        setDragging(true);
        if (!onHandle) {
            moveFocusTo(event.clientX, event.clientY);
        }
    };

    const handlePointerMove = (event) => {
        if (dragging) {
            moveFocusTo(event.clientX, event.clientY);
        }
    };

    const handlePointerUp = (event) => {
        if (dragging) {
            setDragging(false);
            stageRef.current?.releasePointerCapture?.(event.pointerId);
            return;
        }

        const start = tapStartRef.current;
        tapStartRef.current = null;
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) <= TAP_TOLERANCE) {
            moveFocusTo(event.clientX, event.clientY);
        }
    };

    const handlePointerCancel = () => {
        setDragging(false);
        tapStartRef.current = null;
    };

    // Setas movem 1%, com Shift 5%; Home volta o foco ao centro.
    const handleKeyDown = (event) => {
        const step = event.shiftKey ? 5 : 1;
        const moves = {
            ArrowLeft: { positionX: positionX - step },
            ArrowRight: { positionX: positionX + step },
            ArrowUp: { positionY: positionY - step },
            ArrowDown: { positionY: positionY + step },
            Home: { positionX: 50, positionY: 50 },
        };
        const move = moves[event.key];
        if (!move) {
            return;
        }
        event.preventDefault();
        emit({
            positionX: clampFramingPosition(move.positionX ?? positionX),
            positionY: clampFramingPosition(move.positionY ?? positionY),
        });
    };

    const handleImageLoad = (event) => {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (naturalWidth && naturalHeight) {
            setImageAspect(naturalWidth / naturalHeight);
            onImageLoad?.({ width: naturalWidth, height: naturalHeight });
        }
    };

    const isCentered = positionX === 50 && positionY === 50 && zoom === IMAGE_FRAMING_MIN_ZOOM;

    return (
        <div className="image-focus-editor">
            <div className="image-focus-editor__viewport">
                <div
                    ref={stageRef}
                    className={`image-focus-editor__stage${dragging ? ' is-dragging' : ''}`}
                    // Antes de a foto carregar não sabemos a proporção; 3:2 evita o palco pular de tamanho.
                    style={{ '--image-aspect': imageAspect || 1.5 }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerCancel}
                >
                    <img
                        src={src}
                        alt={alt}
                        className="image-focus-editor__image"
                        draggable={false}
                        onLoad={handleImageLoad}
                    />

                    <div className="image-focus-editor__grid" aria-hidden="true" />

                    {imageAspect && frames.map((frame, index) => {
                        const area = getVisibleImageArea({
                            imageAspect,
                            frameAspect: frame.aspectRatio,
                            positionX,
                            positionY,
                            zoom,
                        });
                        return (
                            <div
                                key={frame.key}
                                className={`image-focus-editor__frame image-focus-editor__frame--${index % 2 === 0 ? 'primary' : 'secondary'}`}
                                style={{ left: `${area.left}%`, top: `${area.top}%`, width: `${area.width}%`, height: `${area.height}%` }}
                                aria-hidden="true"
                            >
                                <span>{frame.label}</span>
                            </div>
                        );
                    })}

                    <button
                        type="button"
                        className="image-focus-editor__handle"
                        style={{ left: `${positionX}%`, top: `${positionY}%` }}
                        onKeyDown={handleKeyDown}
                        aria-label={`Ponto de foco em ${positionX}% na horizontal e ${positionY}% na vertical. Use as setas para mover.`}
                    />
                </div>
            </div>

            <div className="image-focus-editor__controls">
                <div className="image-focus-editor__zoom">
                    <label htmlFor={zoomInputId}>Zoom <span>{zoom}%</span></label>
                    <div className="image-focus-editor__zoom-row">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => emit({ zoom: clampFramingZoom(zoom - ZOOM_STEP) })}
                            disabled={zoom <= IMAGE_FRAMING_MIN_ZOOM}
                            aria-label="Diminuir zoom"
                        >
                            <i className="fa-solid fa-minus" aria-hidden="true" />
                        </button>
                        <input
                            id={zoomInputId}
                            type="range"
                            className="form-range"
                            min={IMAGE_FRAMING_MIN_ZOOM}
                            max={IMAGE_FRAMING_MAX_ZOOM}
                            step="1"
                            value={zoom}
                            onChange={(event) => emit({ zoom: clampFramingZoom(event.target.value) })}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => emit({ zoom: clampFramingZoom(zoom + ZOOM_STEP) })}
                            disabled={zoom >= IMAGE_FRAMING_MAX_ZOOM}
                            aria-label="Aumentar zoom"
                        >
                            <i className="fa-solid fa-plus" aria-hidden="true" />
                        </button>
                    </div>
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary image-focus-editor__reset"
                    onClick={() => onChange?.({ ...IMAGE_FRAMING_CENTERED })}
                    disabled={isCentered}
                >
                    <i className="fa-solid fa-rotate-left me-2" aria-hidden="true" />
                    Centralizar
                </button>
            </div>
        </div>
    );
};

export default ImageFocusEditor;
